import { addDays, addMonths, format, startOfMonth, startOfWeek } from 'date-fns';
import type { AdsChartBucket } from '@brand24-original/llm-types';
import { PeriodType } from '../../../shared/types/prompt/PeriodType';
import { MonitorType } from '../../../shared/types/monitor/MonitorType';

/**
 * Channels the Ads tab analyses: the GPT family only (AI Overview stays out
 * until in-answer ads are detectable there). Narrows the owner's features for
 * EffectiveFeaturesProvider and detects projects with no ads-capable model at all.
 * MUST stay in sync with the backend's ADS_CAPABLE_MONITOR_TYPES
 * (apps/backend/src/domain/monitor/ads/services/AdsStatsLocalService.ts).
 */
export const ADS_CAPABLE_MONITOR_TYPES: MonitorType[] = [
  'GPT',
  'GPT_API',
  'GPT_API_BROWSER',
  'GPT_SEARCH_BROWSER',
  'GPT_API_SEARCH_WEB',
];

/**
 * Gap-free bucket keys for the selected range, matching the backend's DATE_TRUNC keys:
 * daily = every day, weekly = Mondays (ISO weeks), monthly = yyyy-MM. Days with no measured
 * answers render as zeros instead of disappearing from the X axis.
 */
export const buildAdsBucketKeys = (from: Date, to: Date, period: PeriodType): string[] => {
  const keys: string[] = [];
  if (period === 'MONTHLY') {
    let current = startOfMonth(from);
    while (current.getTime() <= to.getTime()) {
      keys.push(format(current, 'yyyy-MM'));
      current = addMonths(current, 1);
    }
    return keys;
  }

  let current = period === 'WEEKLY' ? startOfWeek(from, { weekStartsOn: 1 }) : new Date(from);
  const stepDays = period === 'WEEKLY' ? 7 : 1;
  while (current.getTime() <= to.getTime()) {
    keys.push(format(current, 'yyyy-MM-dd'));
    current = addDays(current, stepDays);
  }
  return keys;
};

export const zeroFillAdsBuckets = (keys: string[], buckets: AdsChartBucket[]): AdsChartBucket[] => {
  const byDate = new Map(buckets.map((bucket) => [bucket.date, bucket]));
  return keys.map(
    (date) => byDate.get(date) ?? { date, measured: 0, answersWithAds: 0, adOccurrences: 0 }
  );
};

export type AdsModelFamily = { key: string; label: string; icon: string };

/** Collapses monitor_data.type values of a creative into displayable model families. */
export const adsModelFamilies = (types: string[]): AdsModelFamily[] => {
  const families: AdsModelFamily[] = [];
  if (types.some((type) => type.startsWith('GPT'))) {
    families.push({ key: 'gpt', label: 'ChatGPT', icon: 'ico--gpt-bck' });
  }
  if (types.some((type) => type.startsWith('AI_OVERVIEW'))) {
    families.push({ key: 'ai-overview', label: 'AI Overview', icon: 'ico--ai-overview-bck' });
  }
  return families;
};

/**
 * Ad fields come from scraped third-party ad content — only plain http(s) URLs may reach an
 * href/background-image/img src (a crafted `javascript:` targetUrl would otherwise be a stored XSS).
 */
export const safeExternalUrl = (url: string | null | undefined): string | null =>
  url && /^https?:\/\//i.test(url) ? url : null;

/**
 * Favicon for an advertiser row: the captured ad favicon, else the domain favicon service.
 * Quotes/parens/whitespace are percent-encoded so the value can never break out of the
 * `background-image: url("...")` string it is injected into (scraped values may contain them).
 */
export const advertiserFaviconUrl = (faviconUrl: string | null, domain: string): string =>
  (safeExternalUrl(faviconUrl) ?? `https://favicon.is/${domain}`).replace(
    /["'()\s\\]/g,
    (char) => encodeURIComponent(char)
  );

/**
 * Human-readable form of an ad target URL: host + path only. Captured targetUrls carry huge
 * tracking query strings (utm/oppref/olref) that say nothing to the user and blow up table cells —
 * the full URL stays in the link's href/title.
 */
export const formatDisplayUrl = (url: string): string =>
  url
    .replace(/^https?:\/\/(www\.)?/, '')
    .split(/[?#]/)[0]
    .replace(/\/$/, '');

/**
 * Tile trend against the previous period as TrendTag expects it: `'+N'` (explicit sign, like the
 * Overview tiles), a negative number, or 0 (= no badge). `undefined` previous = nothing measured
 * in the previous period, so no badge either — a "+12" against an unmeasured period would lie.
 */
export const adsTrend = (current: number, previous: number | undefined): string | number => {
  if (previous === undefined) {
    return 0;
  }
  const diff = current - previous;
  return diff > 0 ? `+${diff}` : diff;
};
